import {AxiosInstance} from "axios";
import {Attachment} from "@/attachment/interfaces/attachment";

export interface AttachmentIndexRequest {
    tags?: string[];
}

export interface AttachmentPrefillerOption {
    label: string;
    slug: string;
    /**
     * Without a type, a single item is picked among buttons; checkbox options take several and
     * text options, without items, an optional free text.
     */
    type?: 'checkbox' | 'text';
    items?: {
        label: string;
        value: string | number;
        /** On a checkbox item, flags the part the MSSanté message is about. */
        mssante?: boolean;
    }[]
}

const AttachmentResource = (apiClient: AxiosInstance) => {

    /**
     * Index attachments.
     */
    const index = async (params: AttachmentIndexRequest = {}): Promise<Attachment[]> => {
        return apiClient.get("/attachments", {params})
            .then((response) => response.data.data);
    };

    /**
     * Index prefill attachments.
     */
    const prefillIndex = async (params: AttachmentIndexRequest = {}): Promise<Attachment[]> => {
        return apiClient.get("/prefill-attachments", {params})
            .then((response) => response.data.data);
    };

    /**
     * Get the thumbnail render for a given attachment.
     */
    const thumbnail = async (attachmentId: number) => {
        return apiClient.get(`/attachments/${attachmentId}/thumbnail`, {responseType: 'arraybuffer'})
            .then((response) => response.data);
    };

    /**
     * Render an attachment.
     *
     * @param attachmentId
     */
    const render = async (attachmentId: number) => {
        return apiClient.get(`/attachments/${attachmentId}/render`, {responseType: "blob"})
            .then((response) => response.data);
    };

    /**
     * Render an attachment.
     *
     * @param attachmentId
     * @param consumerKey
     * @param prefillerData
     */
    const prefill = async (attachmentId: number, consumerKey: string, prefillerData?: any) => {
        return apiClient.get(`/attachments/${attachmentId}/prefill`, {
            responseType: "blob",
            params: {consumer_key: consumerKey, additional: prefillerData}
        })
            .then((response) => response.data);
    };

    /**
     * Get prefiller options for an attachment.
     */
    const prefillerOptions = async (attachmentId: number, consumerKey: string): Promise<AttachmentPrefillerOption[]> => {
        return apiClient.get(`/attachments/${attachmentId}/prefiller-options`, {params: {consumer_key: consumerKey}})
            .then(response => response.data.data);
    };

    /**
     * Get consumer attachments index.
     *
     * @param consumerKey
     * @param tags
     */
    const consumerIndex = async (consumerKey: string, tags: string[]): Promise<Attachment[]> => {
        return apiClient.get(`/consumers/${consumerKey}/attachments`, {params: {tags}})
            .then(response => response.data.data);
    };

    return {
        consumerIndex,
        index,
        thumbnail,
        render,
        prefill,
        prefillIndex,
        prefillerOptions,
    };
};

export default AttachmentResource;
